import React, { useMemo, useEffect } from "react";
import {useDarkMode} from "../../contexts/darkModeContext";
import {InformationCircleIcon, PhoneIcon, TruckIcon} from "@heroicons/react/24/outline";
import {Button, Dialog, DialogBackdrop, DialogPanel, DialogTitle, Input} from "@headlessui/react";

const ShortOrderFreight = ({ products, onFreightChange, accountLevel }) => {

    const { darkMode } = useDarkMode();

    const [showBreakdown, setShowBreakdown] = React.useState(false);
    const [freightBreakdown, setFreightBreakdown] = React.useState([]);
    const [hasPersonalInventory, setHasPersonalInventory] = React.useState(false);

    const FreightUMRules = {
        CASE: {
            aliases: ["CAS", "CASE", "CS4", "2.5"],
            moq: 6,
            feePerUnit: 30
        },
        BAG: {
            aliases: ["BAG", "B5", "B12", "B22", "B25", "B30", "B40", "B50", "B51"],
            moq: 20,
            feePerUnit: 30
        },
        GALLON: {
            aliases: ["TR"],
            moq: 100,
            feePerUnit: 5
        }
    };

    const estimateFreightCost = (finalProducts) => {
        // If account level is DIST, disable freight entirely
        if (accountLevel === "DIST") {
            return { total: 0, breakdown: [], hasPersonalInventory: false };
        }
        if (!Array.isArray(finalProducts) || finalProducts.length === 0) return { total: 0, breakdown: [], hasPersonalInventory: false };

        console.log(finalProducts);

        // Track totals by UM rule bucket
        const totals = {};
        const breakdown = [];

        // Track any special/override charges separately so we can zero them out if any MOQ is met
        let specialCharges = 0;
        let hasPersonalInventory = false;

        // Initialize totals
        Object.keys(FreightUMRules).forEach(key => {
            totals[key] = 0;
        });

        finalProducts.forEach(product => {
            const qty = Number(product.quantity || 0);
            const um = (product.selectedVolume || "");
            const name = (product.name || "").toLowerCase();
            const freightMethod = (product.warehouse || "");

            // Only apply freight logic to MAIN or ADL warehouses
            if (!["MAIN", "ADL"].includes(freightMethod)) {
                hasPersonalInventory = true;
                return;
            }

            // Accumulate TOTAL UNITS by UM group (not product count)
            Object.entries(FreightUMRules).forEach(([ruleKey, rule]) => {
                if (rule.aliases.includes(um)) {
                    totals[ruleKey] = (totals[ruleKey] || 0) + qty;
                }
            });

            // Special PolyPhosphite 30® FedEx rule (per‑item override)
            if (
                name.includes("polyphosphite 30") &&
                freightMethod === "fedex" &&
                ["CAS", "CASE", "CS4", "2.5"].includes(um)
            ) {
                specialCharges += qty * 50;
            }
        });

        // If ANY bucket meets MOQ, freight is turned off entirely
        const anyMeetsMOQ = Object.entries(FreightUMRules).some(([key, rule]) => {
            const totalQty = totals[key] || 0;
            return totalQty > 0 && totalQty >= rule.moq;
        });

        let freightTotal = 0;

        Object.entries(FreightUMRules).forEach(([key, rule]) => {
            const totalQty = totals[key];

            if (totalQty > 0) {
                const meetsMOQ = totalQty >= rule.moq;
                const shortage = Math.max(rule.moq - totalQty, 0);

                // If any MOQ is met anywhere, all freight is disabled
                const cost = anyMeetsMOQ ? 0 : (meetsMOQ ? 0 : totalQty * rule.feePerUnit);

                if (!anyMeetsMOQ && !meetsMOQ) {
                    freightTotal += cost;
                }

                breakdown.push({
                    label: key,
                    totalQty,
                    moq: rule.moq,
                    meetsMOQ,
                    shortage,
                    cost,
                    feePerUnit: rule.feePerUnit,
                    freightDisabled: anyMeetsMOQ
                });
            }
        });

        if (!anyMeetsMOQ) {
            freightTotal += specialCharges;
        }

        return { total: anyMeetsMOQ ? 0 : freightTotal, breakdown, hasPersonalInventory };
    };

    const freightResult = useMemo(() => {
        return estimateFreightCost(products);
    }, [products]);

    useEffect(() => {
        if (!freightResult) return;

        setFreightBreakdown(freightResult.breakdown);
        setHasPersonalInventory(freightResult.hasPersonalInventory);

        if (typeof onFreightChange === "function") {
            onFreightChange(freightResult.total);
        }
    }, [freightResult, onFreightChange]);
    
    return (
        <div className={`w-full border rounded-lg ${darkMode ? "border-neutral-700" : ""}`}>

            <div className={`rounded-t-lg px-3 py-2 border-b ${darkMode ? 'bg-darkBgColor border-neutral-700' : 'bg-bgColor'} flex justify-between items-center`}>
                <span className={`font-bold`}>Short Order Freight</span>
                <TruckIcon className={`text-gray-500 size-6`}/>
                {/*<span className={`text-gray-500 text-xs`}>Estimate</span>*/}
            </div>
            
            <div className={`p-3 flex justify-between items-center relative`}>
                <span className={`font-semibold`}>
                    ${freightResult.total.toFixed(2)}
                </span>

                <div className="flex items-center gap-2">
                    <span className={`text-gray-500 text-xs`}>
                        Estimated Freight Costs
                    </span>

                    <InformationCircleIcon
                        onClick={() => setShowBreakdown(true)}
                        className="size-5 text-gray-500 hover:text-gray-600 cursor-pointer"
                    />
                </div>
            </div>

           
            <Dialog open={showBreakdown} onClose={setShowBreakdown} className="relative z-50">
              <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-500/50 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
              />
        
              <div className="fixed inset-0 z-10 font-mono w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                  <DialogPanel
                    transition
                    className={`relative transform overflow-hidden rounded-lg ${darkMode ? 'bg-darkMainColor text-white' : 'bg-white text-black'} px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-2xl sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95`}
                  >
                    <div className="sm:flex sm:items-start">
                      <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-green-500/15 sm:mx-0 sm:size-10">
                        <TruckIcon aria-hidden="true" className="size-6 text-green-500" />
                      </div>
                      <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                        <DialogTitle as="h3" className="text-base font-semibold">
                          Freight Cost Breakdown
                        </DialogTitle>
                          <div className="mt-2">
                              <p className="text-sm text-gray-500 mb-2">
                                  This breakdown explains how freight charges are calculated and identifies the factors contributing to the applied freight cost.
                              </p>
                              
                              {hasPersonalInventory && (
                                  <div className="text-xs text-blue-500 mb-2">
                                      Some products are sourced from personal inventory and are excluded from freight calculations.
                                  </div>
                              )}

                              {freightBreakdown.length === 0 && (
                                  <div className="text-gray-500 text-xs">
                                      No freight charges applied.
                                  </div>
                              )}
        
                              {freightBreakdown.map((item, index) => {
                                  const progress = Math.min((item.totalQty / item.moq) * 100, 100);

                                  return (
                                      <div
                                          key={index}
                                          className={`mb-4 last:mb-0 rounded-md`}
                                      >
                                          <div className="flex justify-between items-center mb-1">
                                              <span className="font-medium text-sm">{item.label}</span>
                                              <span
                                                  className={`text-xs font-semibold ${
                                                      item.meetsMOQ ? 'text-green-500' : 'text-orange-500'
                                                  }`}
                                              >
                                                  {item.meetsMOQ ? 'MOQ Met' : 'Below MOQ'}
                                              </span>
                                          </div>

                                          <div className="text-xs text-gray-500 mb-2">
                                              {item.totalQty} of {item.moq} units | {((item.totalQty / item.moq) * 100).toFixed(0)}% of MOQ
                                          </div>

                                          {/* Progress Bar */}
                                          <div className="w-full h-2 rounded-full bg-gray-200 dark:bg-neutral-700 overflow-hidden mb-2">
                                              <div
                                                  className={`h-full rounded-full transition-all duration-300 ${
                                                      item.meetsMOQ ? 'bg-green-500' : 'bg-orange-400'
                                                  }`}
                                                  style={{ width: `${progress}%` }}
                                              />
                                          </div>

                                          {!item.meetsMOQ && !item.freightDisabled && (
                                              <div className="text-xs text-gray-500">
                                                  {item.shortage} more needed · ${item.feePerUnit}/unit → ${item.cost.toFixed(2)}
                                              </div>
                                          )}

                                          {item.freightDisabled && (
                                              <div className="text-xs text-green-600 mt-1">
                                                  Freight waived due to MOQ being met in another category
                                              </div>
                                          )}
                                      </div>
                                  );
                              })}
                              
                          </div>
                      </div>
                    </div>
                      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                          <Button
                              type="button"
                              onClick={() => setShowBreakdown(false)}
                              className={`inline-flex w-full justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-opacity-50 sm:ml-3 sm:w-auto`}
                          >
                              I Understand
                          </Button>
                      </div>
                  </DialogPanel>
                </div>
              </div>
            </Dialog>

            
            
        </div>
    )
}

export default ShortOrderFreight;
